/**
 * Browser token session: login, ROTATING refresh, revoke, and an authorized
 * fetch that heals a 401 exactly once.
 *
 * PORTED FROM `frontend/mobile/src/lib/auth/session.ts`, deliberately and almost
 * literally — the mobile app already solved this against the same FastAPI
 * endpoints, and the two hard properties below (rotation, reuse detection) are
 * properties of the SERVER, so they bind the web client identically. Three
 * things changed and nothing else:
 *
 *   1. `SecureStorage` became `TokenStorage` (browser `localStorage`; see
 *      `lib/storage/token-storage.ts` for the choice and its XSS cost).
 *   2. `authorizedFetch` also sends cookies (`credentials: 'include'`), because
 *      for the length of v2.2 half the API is still Next.js routes that
 *      authenticate by Auth.js cookie and ignore the bearer entirely (D-001).
 *   3. `deviceId` is a per-browser install id — see `device-id.ts`.
 *
 * Contract: `backend/api/app/routers/mobile_auth.py` +
 * `backend/api/app/services/mobile_auth.py`.
 *
 *   access token  - stateless JWT, 15 min, sent as `Authorization: Bearer`
 *   refresh token - opaque, 60 days, ROTATED on every use
 *
 * Two properties of that backend dictate the design of this file:
 *
 * 1. **Rotation.** `refresh_mobile_session` revokes the presented token and
 *    issues a new one. The new token must reach secure storage or the user is
 *    signed out for real - the old one is already dead server-side. Hence the
 *    whole session is ONE JSON blob under ONE key, written in a single
 *    `setItem`, and the in-memory copy is only advanced after that write
 *    resolves. A write that fails is fail-closed: the session is cleared rather
 *    than left holding a token the server has already revoked.
 *
 * 2. **Reuse detection.** Presenting an already-revoked refresh token makes the
 *    backend revoke the ENTIRE device family (OAuth 2.0 BCP). So the old token
 *    may be sent at most once, ever. That is what the single-flight guard below
 *    is for: ten requests racing on a 401 join ONE refresh instead of firing ten
 *    rotations, nine of which would be reuse and would log the user out.
 *
 * This module imports nothing native - storage and `fetch` are injected - so it
 * runs in a plain Node test environment.
 */

import { domainBaseUrl } from '../api/config';
import type { TokenStorage } from '../storage/token-storage';
import {
  AuthError,
  type AuthErrorCode,
  type MobileTokenPair,
  type StoredSession,
} from './types';

/** Single key: one blob, one write, no half-written session. */
export const SESSION_STORAGE_KEY = 'aim.session.v1';

/** Refresh this long before the access token actually expires. */
export const ACCESS_TOKEN_SKEW_MS = 60_000;

export interface AuthSessionOptions {
  storage: TokenStorage;
  /** Stable per-browser id. Sent on login / revoke. */
  deviceId: string;
  /** Human label for the "signed-in devices" list, e.g. "Chrome on Windows". */
  deviceName?: string | null;
  fetchImpl?: typeof fetch;
  now?: () => number;
  /** Defaults to the `mobileAuth` domain base URL. */
  baseUrl?: string;
  /**
   * Base URL for the `auth` domain (`/py-api/v1/auth`) — MFA-login
   * completion, the devices list, and change-password all live here, NOT
   * under `mobileAuth`. Defaults to the `auth` domain base URL; overridable
   * for tests exactly like `baseUrl`.
   */
  authBaseUrl?: string;
  storageKey?: string;
  /** Fired on every session transition, including sign-out (`null`). */
  onChange?: (session: StoredSession | null) => void;
}

interface ErrorBody {
  error?: string;
  details?: unknown;
  /** Google exchange only — see `AuthErrorCode`. */
  code?: string;
  /** Google exchange only — the address Google verified. */
  email?: string;
}

/** Server `code` values the Google exchange can answer with. */
const GOOGLE_ERROR_CODES = new Set([
  'invalid_token',
  'access_request_pending',
  'account_disabled',
  'google_disabled',
]);

function isTokenPair(value: unknown): value is MobileTokenPair {
  if (typeof value !== 'object' || value === null) return false;
  const pair = value as Partial<MobileTokenPair>;
  return (
    typeof pair.accessToken === 'string' &&
    typeof pair.refreshToken === 'string' &&
    typeof pair.expiresIn === 'number' &&
    typeof pair.refreshTokenExpiresAt === 'string' &&
    typeof pair.user === 'object' &&
    pair.user !== null
  );
}

/** Body shape of `POST /login` when a second factor is enrolled. */
interface MfaChallenge {
  mfaRequired: true;
  mfaToken: string;
}

function isMfaChallenge(value: unknown): value is MfaChallenge {
  if (typeof value !== 'object' || value === null) return false;
  const body = value as Partial<MfaChallenge>;
  return body.mfaRequired === true && typeof body.mfaToken === 'string' && body.mfaToken.length > 0;
}

function isStoredSession(value: unknown): value is StoredSession {
  if (typeof value !== 'object' || value === null) return false;
  const s = value as Partial<StoredSession>;
  return (
    typeof s.accessToken === 'string' &&
    typeof s.refreshToken === 'string' &&
    typeof s.accessTokenExpiresAt === 'number' &&
    typeof s.refreshTokenExpiresAt === 'number' &&
    typeof s.user === 'object' &&
    s.user !== null
  );
}

/**
 * Wave 3 (W12) — pure surface for `MobileUser.mustChangePassword`.
 *
 * This is deliberately the ENTIRE extent of this file's involvement in the
 * force-password-change gate: it reads the flag off whatever session is
 * already in hand. Deciding what to DO with a `true` (redirect to
 * `ForcePasswordChangePage`, block navigation elsewhere) is a routing
 * concern — `AuthProvider` / `route-guard.ts` are not owned by this change,
 * so that wiring is not here. See `ForcePasswordChangePage.tsx`'s header for
 * the recommended integration.
 */
export function requiresPasswordChange(session: StoredSession | null | undefined): boolean {
  return Boolean(session?.user?.mustChangePassword);
}

export function toStoredSession(pair: MobileTokenPair, nowMs: number): StoredSession {
  const refreshExpiry = Date.parse(pair.refreshTokenExpiresAt);
  return {
    accessToken: pair.accessToken,
    accessTokenExpiresAt: nowMs + pair.expiresIn * 1000,
    refreshToken: pair.refreshToken,
    // A server that ever sends an unparseable date must not brick the client:
    // fall back to the documented 60-day life rather than expiring instantly.
    refreshTokenExpiresAt: Number.isNaN(refreshExpiry)
      ? nowMs + 60 * 24 * 60 * 60 * 1000
      : refreshExpiry,
    user: pair.user,
  };
}

export class AuthSession {
  private readonly storage: TokenStorage;
  private readonly fetchImpl: typeof fetch;
  private readonly now: () => number;
  private readonly baseUrl: string;
  private readonly storageKey: string;
  private readonly onChange?: (session: StoredSession | null) => void;

  readonly deviceId: string;
  readonly deviceName: string | null;

  private session: StoredSession | null = null;
  private hydrated = false;
  /** THE single-flight guard. Non-null while a rotation is in flight. */
  private refreshInFlight: Promise<StoredSession> | null = null;

  constructor(options: AuthSessionOptions) {
    this.storage = options.storage;
    this.deviceId = options.deviceId;
    this.deviceName = options.deviceName ?? null;
    this.fetchImpl = options.fetchImpl ?? ((input: string | URL | Request, init?: RequestInit) => fetch(input, init));
    this.now = options.now ?? (() => Date.now());
    this.baseUrl = options.baseUrl ?? domainBaseUrl('mobileAuth');
    this.storageKey = options.storageKey ?? SESSION_STORAGE_KEY;
    this.onChange = options.onChange;
  }

  // -- state ----------------------------------------------------------------

  /** In-memory session. `hydrate()` first if the app has just started. */
  getSession(): StoredSession | null {
    return this.session;
  }

  getUser() {
    return this.session?.user ?? null;
  }

  isSignedIn(): boolean {
    return this.session !== null && this.session.refreshTokenExpiresAt > this.now();
  }

  /** Read the persisted session once at startup. Tolerates junk in storage. */
  async hydrate(): Promise<StoredSession | null> {
    if (this.hydrated) return this.session;

    let raw: string | null = null;
    try {
      raw = await this.storage.getItem(this.storageKey);
    } catch {
      raw = null; // A keystore that will not open is the same as no session.
    }
    this.hydrated = true;
    if (!raw) return null;

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      await this.clear();
      return null;
    }
    if (!isStoredSession(parsed)) {
      await this.clear();
      return null;
    }
    // A dead refresh token cannot be rotated - sending it would be reuse.
    if (parsed.refreshTokenExpiresAt <= this.now()) {
      await this.clear();
      return null;
    }

    this.session = parsed;
    this.onChange?.(parsed);
    return parsed;
  }

  // -- persistence ----------------------------------------------------------

  /**
   * Write, THEN advance memory. The single `setItem` is the atomic unit: the
   * blob either lands whole or not at all, so there is no state in which the
   * access token is new and the refresh token is old.
   */
  private async persist(next: StoredSession): Promise<StoredSession> {
    try {
      await this.storage.setItem(this.storageKey, JSON.stringify(next));
    } catch {
      // The server has already rotated. A token we cannot store is a token we
      // must not keep using, and the previous one is dead. Fail closed.
      await this.clear();
      throw new AuthError(
        'Could not save your session securely. Please sign in again.',
        'persist_failed',
        null,
      );
    }
    this.session = next;
    this.hydrated = true;
    this.onChange?.(next);
    return next;
  }

  /** Drop the session locally. Never throws. */
  async clear(): Promise<void> {
    this.session = null;
    this.hydrated = true;
    try {
      await this.storage.removeItem(this.storageKey);
    } catch {
      // Best effort - the in-memory session is already gone.
    }
    this.onChange?.(null);
  }

  // -- HTTP helpers ---------------------------------------------------------

  private async postJson(
    path: string,
    body: unknown,
    accessToken?: string,
    base: string = this.baseUrl,
  ): Promise<Response> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    try {
      return await this.fetchImpl(`${base}${path}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
    } catch {
      throw new AuthError(
        'Could not reach the server. Check your connection and try again.',
        'network',
      );
    }
  }

  private static async readError(res: Response): Promise<string> {
    try {
      const body = (await res.json()) as ErrorBody;
      if (typeof body?.error === 'string' && body.error) return body.error;
    } catch {
      // Non-JSON error body - fall through to the generic message.
    }
    return 'Something went wrong. Please try again.';
  }

  /** Parse a JSON body once. Split out of `readPair` so `login()` can inspect
   *  the body for an MFA challenge BEFORE deciding it must be a token pair. */
  private static async parseJson(res: Response): Promise<unknown> {
    try {
      return await res.json();
    } catch {
      throw new AuthError('The server sent an unreadable response.', 'server', res.status);
    }
  }

  private static pairFromBody(body: unknown, status: number): MobileTokenPair {
    if (!isTokenPair(body)) {
      throw new AuthError('The server sent an unexpected response.', 'server', status);
    }
    return body;
  }

  private static async readPair(res: Response): Promise<MobileTokenPair> {
    return AuthSession.pairFromBody(await AuthSession.parseJson(res), res.status);
  }

  // -- login ----------------------------------------------------------------

  /**
   * Exchange email + password for a token pair.
   *
   * Unknown email, wrong password and a deactivated account all answer 401 with
   * the SAME opaque message by design (`INVALID_CREDENTIALS`) - the client must
   * not try to tell them apart, and does not.
   *
   * Wave 3 (W12): a correct password on an account with MFA enrolled answers
   * 200 with `{ mfaRequired: true, mfaToken }` instead of a token pair — no
   * session is persisted yet. That is surfaced as an `AuthError('mfa_required')`
   * carrying `mfaToken`, the same pattern the Google refusals already use, so
   * every existing catch-on-`code` call site needs only one more branch.
   */
  async login(email: string, password: string): Promise<StoredSession> {
    const res = await this.postJson('/login', {
      email: email.trim(),
      password,
      deviceId: this.deviceId,
      ...(this.deviceName ? { deviceName: this.deviceName } : {}),
    });

    if (!res.ok) {
      const message = await AuthSession.readError(res);
      if (res.status === 400) throw new AuthError(message, 'validation', 400);
      if (res.status === 401) throw new AuthError(message, 'invalid_credentials', 401);
      throw new AuthError(message, 'server', res.status);
    }

    const body = await AuthSession.parseJson(res);
    if (isMfaChallenge(body)) {
      throw new AuthError(
        'Enter your 6-digit authentication code to finish signing in.',
        'mfa_required',
        res.status,
        null,
        body.mfaToken,
      );
    }

    const pair = AuthSession.pairFromBody(body, res.status);
    return this.persist(toStoredSession(pair, this.now()));
  }

  // -- MFA login completion ---------------------------------------------------

  /**
   * Finish a login that `login()` interrupted with `mfa_required`.
   *
   * `code` is either the 6-digit TOTP code or one of the 10 recovery codes —
   * the server accepts either at this one endpoint, so this file does not
   * branch on which the user typed. Success returns the SAME token-pair shape
   * `login()` and `loginWithGoogle()` return, persisted the same single way.
   *
   * Deliberately hits `authBaseUrl` (`/py-api/v1/auth/mfa`), NOT `baseUrl`
   * (`/py-api/v1/mobile/auth`) — MFA-login completion is `aim_auth.py`'s
   * route, not `mobile_auth.py`'s, per the W11 contract.
   */
  async completeMfaLogin(mfaToken: string, code: string): Promise<StoredSession> {
    // Trust release: `/mobile/auth/mfa/login` (the token-pair route, the
    // default `baseUrl`) -- `/auth/mfa/login` only sets the legacy cookie and
    // returns no refresh token, so `readPair` could never accept it.
    // A recovery code is `XXXXXXXX-XXXXXXXX`; the server wants it as `recoveryCode`.
    const clean = code.trim();
    const isRecovery = clean.includes('-') || clean.length > 8;
    const res = await this.postJson('/mfa/login', {
      mfaToken,
      deviceId: this.deviceId,
      ...(this.deviceName ? { deviceName: this.deviceName } : {}),
      ...(isRecovery ? { recoveryCode: clean.toUpperCase() } : { code: clean }),
    });

    if (!res.ok) {
      const message = await AuthSession.readError(res);
      if (res.status === 400 || res.status === 401) {
        throw new AuthError(message, 'invalid_credentials', res.status);
      }
      throw new AuthError(message, 'server', res.status);
    }

    const pair = await AuthSession.readPair(res);
    return this.persist(toStoredSession(pair, this.now()));
  }

  // -- Google sign-in -------------------------------------------------------

  /**
   * Exchange a Google ID token for the SAME token pair `login()` returns.
   *
   * That sameness is the whole design. Google replaces the *proof of identity*
   * and nothing else: what comes back is the identical 15-minute access token
   * and 60-day rotating refresh token, stored under the same key, rotated by
   * the same `refresh()`, revoked by the same `logout()`. No branch anywhere
   * downstream of this method knows which button the user pressed.
   *
   * `credential` is passed through untouched and is never decoded, stored, or
   * logged here — it is spent once, server-side, and its verification is the
   * server's job (`google_auth_tokens.verify_google_id_token`).
   *
   * The three refusals below are NOT failures to report as "sign-in broken":
   * an unknown email means a request is now waiting for an admin, and a
   * disabled account means exactly that. Both are surfaced with their code so
   * the login page can send the person somewhere useful.
   */
  async loginWithGoogle(credential: string): Promise<StoredSession> {
    const res = await this.postJson('/google', {
      credential,
      deviceId: this.deviceId,
      ...(this.deviceName ? { deviceName: this.deviceName } : {}),
    });

    if (!res.ok) {
      const failure = await AuthSession.readFailure(res);
      const code =
        failure.code && GOOGLE_ERROR_CODES.has(failure.code)
          ? (failure.code as AuthErrorCode)
          : res.status === 400
            ? 'validation'
            : 'server';
      throw new AuthError(failure.message, code, res.status, failure.email);
    }

    const pair = await AuthSession.readPair(res);
    return this.persist(toStoredSession(pair, this.now()));
  }

  /** `readError`, plus the `code`/`email` the Google exchange adds. */
  private static async readFailure(
    res: Response,
  ): Promise<{ message: string; code: string | null; email: string | null }> {
    try {
      const body = (await res.json()) as ErrorBody;
      return {
        message:
          typeof body?.error === 'string' && body.error
            ? body.error
            : 'Something went wrong. Please try again.',
        code: typeof body?.code === 'string' ? body.code : null,
        email: typeof body?.email === 'string' ? body.email : null,
      };
    } catch {
      return {
        message: 'Something went wrong. Please try again.',
        code: null,
        email: null,
      };
    }
  }

  // -- refresh (single-flight, rotating) ------------------------------------

  /**
   * Rotate the refresh token. Concurrent callers share ONE request.
   *
   * The guard is assigned synchronously - before any `await` - so callers that
   * arrive in the same tick, or in any tick before the rotation settles, all
   * observe the same promise. Exactly one HTTP rotation happens per generation
   * of refresh token, which is what keeps the reuse detection in the backend
   * from revoking the whole device family.
   */
  refresh(): Promise<StoredSession> {
    if (this.refreshInFlight) return this.refreshInFlight;

    const inFlight = this.performRefresh();
    this.refreshInFlight = inFlight;
    // Cleared once settled, so the NEXT expiry starts a fresh single flight.
    void inFlight
      .catch(() => undefined)
      .then(() => {
        if (this.refreshInFlight === inFlight) this.refreshInFlight = null;
      });
    return inFlight;
  }

  private async performRefresh(): Promise<StoredSession> {
    const current = this.session ?? (await this.hydrate());
    if (!current) {
      throw new AuthError('You are signed out. Please sign in again.', 'no_session', 401);
    }
    if (current.refreshTokenExpiresAt <= this.now()) {
      await this.clear();
      throw new AuthError('Your session has expired. Please sign in again.', 'refresh_failed', 401);
    }

    const res = await this.postJson('/refresh', { refreshToken: current.refreshToken });

    if (!res.ok) {
      const message = await AuthSession.readError(res);
      // 400/401 both mean this token will never work again. Anything else
      // (500, 502) is transient: keep the session so a retry can succeed.
      if (res.status === 400 || res.status === 401) {
        await this.clear();
        throw new AuthError(message, 'refresh_failed', res.status);
      }
      throw new AuthError(message, 'server', res.status);
    }

    const pair = await AuthSession.readPair(res);
    return this.persist(toStoredSession(pair, this.now()));
  }

  /**
   * Refresh only if the access token of the caller is still the current one.
   *
   * A request that 401s while a sibling request has ALREADY completed a
   * rotation must simply retry with the new token - refreshing again would
   * throw away a perfectly good token pair for nothing.
   */
  private async refreshIfStale(usedAccessToken: string | null): Promise<StoredSession> {
    const current = this.session;
    if (current && usedAccessToken && current.accessToken !== usedAccessToken) {
      return current;
    }
    return this.refresh();
  }

  // -- access token ---------------------------------------------------------

  /**
   * A usable access token, refreshing proactively inside the skew window so a
   * request is not spent discovering the token expired 200 ms ago.
   */
  async getAccessToken(): Promise<string> {
    const current = this.session ?? (await this.hydrate());
    if (!current) {
      throw new AuthError('You are signed out. Please sign in again.', 'no_session', 401);
    }
    if (current.accessTokenExpiresAt - this.now() > ACCESS_TOKEN_SKEW_MS) {
      return current.accessToken;
    }
    const rotated = await this.refresh();
    return rotated.accessToken;
  }

  /**
   * `fetch` with the bearer token attached, healing one 401.
   *
   * The 401 path is where the ten-concurrent-requests case lives: each caller
   * reports the token it used, `refreshIfStale` collapses them onto the single
   * in-flight rotation, and each then retries ONCE with the rotated token. A
   * second 401 is a real authorization failure and is returned to the caller.
   */
  async authorizedFetch(input: string, init: RequestInit = {}): Promise<Response> {
    const token = await this.getAccessToken();
    const first = await this.fetchWithToken(input, init, token);
    if (first.status !== 401) return first;

    const rotated = await this.refreshIfStale(token);
    return this.fetchWithToken(input, init, rotated.accessToken);
  }

  private fetchWithToken(input: string, init: RequestInit, token: string): Promise<Response> {
    const headers = new Headers(init.headers ?? {});
    headers.set('authorization', `Bearer ${token}`);
    // `credentials: 'include'` is the v2.2 transition tax. FastAPI routes read
    // the bearer and ignore the cookie; the Next.js routes that have not been
    // ported yet do the exact opposite (`requireAuth()` -> `auth()` -> Auth.js
    // cookie) and would answer 401 to a bearer-only request. Sending both
    // credentials means one `apiFetch` serves both backends, and the day a
    // domain moves the call site does not change. Same origin, so this is not
    // a cross-site cookie.
    return this.fetchImpl(input, { credentials: 'include', ...init, headers });
  }

  // -- revoke ---------------------------------------------------------------

  /** Normal sign-out: revoke this token server-side, then clear locally. */
  async logout(): Promise<void> {
    const current = this.session;
    if (current) {
      try {
        const token = await this.getAccessToken();
        await this.postJson('/revoke', { refreshToken: current.refreshToken }, token);
      } catch {
        // A sign-out must never fail. The access token expires in <= 15 min
        // anyway and the refresh token is dropped locally on the next line.
      }
    }
    await this.clear();
  }

  /** Lost-laptop path: revoke every signed-in device for this user. */
  async revokeAllDevices(): Promise<number> {
    const token = await this.getAccessToken();
    const res = await this.postJson('/revoke', { allDevices: true }, token);
    if (!res.ok) {
      throw new AuthError(await AuthSession.readError(res), 'server', res.status);
    }
    const body = (await res.json()) as { revoked?: number };
    await this.clear();
    return typeof body?.revoked === 'number' ? body.revoked : 0;
  }

  /** Revoke one named device (e.g. a phone) without signing this browser out. */
  async revokeDevice(deviceId: string): Promise<number> {
    const token = await this.getAccessToken();
    const res = await this.postJson('/revoke', { deviceId }, token);
    if (!res.ok) {
      throw new AuthError(await AuthSession.readError(res), 'server', res.status);
    }
    const body = (await res.json()) as { revoked?: number };
    return typeof body?.revoked === 'number' ? body.revoked : 0;
  }
}
